import * as React from 'react';
import { useState } from 'react';

import { Card, CardContent } from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import { useMasterContext } from '&/master';
import { useCarriersForSelect } from '&/features/settings/carriers/hooks';
import { useSitesForSelect } from '&/features/settings/sites/hooks';
import { UserDrawer } from '../../users/components/user-drawer';
import { DriversActionBar, DriversTable } from '../components';
import { useDriversPage } from '../hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriversPageProps {
  carrierId?: string;
  /**
   * Explicit scope from the route params (site/space settings pages). Takes precedence over
   * the master context: the breadcrumb scope can point elsewhere when the user browses into
   * an entity's settings via a list, which would otherwise leave the drawer's scope unlocked
   * (and query the wrong space).
   */
  siteId?: string;
  spaceId?: string;
}

type DriversContext = 'carrier' | 'site' | 'space';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveContext(carrierId: string | undefined, siteId: string | null | undefined): DriversContext {
  if (carrierId) return 'carrier';
  if (siteId) return 'site';
  return 'space';
}

function toSpaceIdArg(spaceId: string | null | undefined): string | undefined {
  return spaceId ?? undefined;
}

// Route-provided scope wins over the master context: the breadcrumb can point elsewhere when
// the user browses into an entity's settings via a list. When a space page declares spaceId,
// no site is inherited from the breadcrumb either.
function resolveScope(
  siteIdProp: string | undefined,
  spaceIdProp: string | undefined,
  ctx: { siteId: string | null; spaceId: string | null },
): { siteId: string | null; spaceId: string | null } {
  return {
    siteId: siteIdProp ?? (spaceIdProp ? null : ctx.siteId),
    spaceId: spaceIdProp ?? ctx.spaceId,
  };
}

function toScopeOptions(context: DriversContext, sitesForSelect: MultiSelectOption[]): MultiSelectOption[] | undefined {
  if (context === 'site') return undefined;
  return sitesForSelect;
}

function toCarrierOptions(
  context: DriversContext,
  carriersForSelect: MultiSelectOption[],
): MultiSelectOption[] | undefined {
  if (context === 'carrier') return undefined;
  return carriersForSelect;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DriversPage({
  carrierId,
  siteId: siteIdProp,
  spaceId: spaceIdProp,
}: DriversPageProps = {}): React.JSX.Element {
  const masterContext = useMasterContext();
  const { siteId, spaceId } = resolveScope(siteIdProp, spaceIdProp, masterContext);

  const context = deriveContext(carrierId, siteId);
  const spaceIdArg = toSpaceIdArg(spaceId);
  const fetchCarriers = context === 'site' || context === 'space';
  const fetchSites = context === 'carrier' || context === 'space';

  const pageState = useDriversPage({ carrierId, enabled: true, siteId, spaceId: spaceIdArg });

  const carriersForSelect = useCarriersForSelect({
    enabled: fetchCarriers,
    enabledOnSiteId: siteId ?? undefined,
    spaceId: siteId ? undefined : spaceIdArg,
  });
  const { options: sitesForSelect } = useSitesForSelect({
    enabled: fetchSites,
    spaceIds: spaceIdArg ? [spaceIdArg] : undefined,
  });

  const scopeOptions = toScopeOptions(context, sitesForSelect);
  const carrierOptions = toCarrierOptions(context, carriersForSelect);

  const [drawerOpen, setDrawerOpen] = useState(false);

  if (pageState.error) {
    throw pageState.error;
  }

  function handleAdd(): void {
    setDrawerOpen(true);
  }

  function handleDrawerClose(): void {
    setDrawerOpen(false);
  }

  const pagination = {
    currentPage: pageState.currentPage,
    onPageChange: pageState.onPageChange,
    pageSize: pageState.pageSize,
    totalItems: pageState.totalItems,
    totalPages: pageState.totalPages,
  };

  const sorting = {
    onSort: pageState.onSort,
    sortBy: pageState.sortBy,
    sortDirection: pageState.sortDirection,
  };

  return (
    <div className="flex h-full flex-1 flex-col gap-4">
      <DriversActionBar
        carrierOptions={carrierOptions}
        carrierValues={pageState.carrierFilter}
        onAdd={handleAdd}
        onCarrierChange={pageState.onCarrierChange}
        onScopeChange={pageState.onScopeChange}
        onSearchChange={pageState.onSearchChange}
        onStatusChange={pageState.onStatusChange}
        scopeOptions={scopeOptions}
        scopeValues={pageState.scopeValues}
        searchValue={pageState.searchValue}
        statusValues={pageState.statusValues}
      />
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <DriversTable
            carrierOptions={carrierOptions}
            drivers={pageState.drivers}
            isLoading={pageState.isLoading}
            pagination={pagination}
            sorting={sorting}
          />
        </CardContent>
      </Card>
      <UserDrawer
        entryPoint="drivers"
        lockedCarrierId={carrierId}
        lockedSiteId={siteId ?? undefined}
        lockedSpaceId={spaceIdArg}
        onClose={handleDrawerClose}
        open={drawerOpen}
        user={null}
      />
    </div>
  );
}
